import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDownloadURL, ref } from 'firebase/storage';
import { firebase } from '../lib/firebase';
import { useAuth } from '../lib/useAuth';
import {
  adminListUserSnippets,
  adminListUsers,
  isAdmin,
  type AdminSnippetsPage,
  type AdminUser,
} from '../lib/admin';

export default function Admin() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openUid, setOpenUid] = useState<string | null>(null);

  useEffect(() => {
    if (auth.status === 'signed-out') {
      navigate('/signin?next=/admin', { replace: true });
    }
  }, [auth.status, navigate]);

  useEffect(() => {
    if (auth.status !== 'signed-in' || !isAdmin(auth.user.email)) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await adminListUsers();
        if (!cancelled) {
          list.sort((a, b) => b.snippetsTotal - a.snippetsTotal);
          setUsers(list);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  if (auth.status === 'loading') {
    return (
      <div className="mx-auto max-w-5xl px-6 py-16">
        <p className="text-sm text-neutral-400">Loading…</p>
      </div>
    );
  }

  if (auth.status === 'signed-out') return null;

  if (!isAdmin(auth.user.email)) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-16">
        <p className="text-sm text-red-400">Not authorized.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <h1 className="text-3xl font-semibold tracking-tight">Admin</h1>
      <p className="mt-1 text-sm text-neutral-400">
        All accounts and their snippit counts.
      </p>

      {error && <p className="mt-8 text-sm text-red-400">{error}</p>}
      {users === null && !error && (
        <p className="mt-8 text-sm text-neutral-400">Loading users…</p>
      )}

      {users && (
        <div className="mt-8 overflow-hidden rounded-lg border border-neutral-800">
          <table className="w-full text-left text-sm">
            <thead className="bg-neutral-900 text-xs uppercase tracking-wide text-neutral-400">
              <tr>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Snippits</th>
                <th className="px-4 py-2 font-medium">Storage</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2 font-medium">Last sign-in</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.uid}
                  user={u}
                  isOpen={openUid === u.uid}
                  onToggle={() => setOpenUid(openUid === u.uid ? null : u.uid)}
                />
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-neutral-400">
                    No users.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserRow({
  user,
  isOpen,
  onToggle,
}: {
  user: AdminUser;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="border-t border-neutral-800">
        <td className="px-4 py-2 text-neutral-200">
          {user.email ?? <span className="text-neutral-500">(no email)</span>}
          {user.disabled && (
            <span className="ml-2 rounded bg-red-950 px-1.5 py-0.5 text-xs text-red-300">
              disabled
            </span>
          )}
        </td>
        <td className="px-4 py-2 tabular-nums">{user.snippetsTotal}</td>
        <td className="px-4 py-2 tabular-nums text-neutral-400">
          {formatBytes(user.storageBytes)}
        </td>
        <td className="px-4 py-2 text-neutral-400">{formatDate(user.createdAt)}</td>
        <td className="px-4 py-2 text-neutral-400">{formatDate(user.lastSignInAt)}</td>
        <td className="px-4 py-2 text-right">
          {user.snippetsTotal > 0 && (
            <button
              onClick={onToggle}
              className="rounded px-2 py-1 text-xs text-neutral-300 hover:bg-neutral-800"
            >
              {isOpen ? 'Hide' : 'View'}
            </button>
          )}
        </td>
      </tr>
      {isOpen && (
        <tr className="border-t border-neutral-800 bg-neutral-950">
          <td colSpan={6} className="px-4 py-4">
            <UserSnippets uid={user.uid} />
          </td>
        </tr>
      )}
    </>
  );
}

function UserSnippets({ uid }: { uid: string }) {
  const [page, setPage] = useState<AdminSnippetsPage | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await adminListUserSnippets(uid);
        if (cancelled) return;
        setPage(result);
        const entries = await Promise.all(
          result.snippets.map(async (s) => {
            try {
              const url = await getDownloadURL(ref(firebase.storage, s.storagePath));
              return [s.id, url] as const;
            } catch {
              return [s.id, ''] as const;
            }
          }),
        );
        if (!cancelled) setThumbs(Object.fromEntries(entries));
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (page === null) return <p className="text-sm text-neutral-400">Loading…</p>;
  if (page.snippets.length === 0) return <p className="text-sm text-neutral-400">No snippits.</p>;

  return (
    <div>
      {page.hasMore && (
        <p className="mb-3 text-xs text-neutral-400">
          Showing first {page.limit} snippits (most recent). More exist.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {page.snippets.map((s) => (
          <Link
            key={s.id}
            to={`/s/${s.id}`}
            className="group block overflow-hidden rounded-md border border-neutral-800 bg-neutral-900 hover:border-neutral-700"
          >
            <div className="aspect-video bg-neutral-950">
              {thumbs[s.id] && (
                <img
                  src={thumbs[s.id]}
                  alt={s.title}
                  className="h-full w-full object-contain"
                />
              )}
            </div>
            <div className="px-2 py-1.5 text-xs">
              <div className="truncate text-neutral-300">{s.title}</div>
              <div className="text-neutral-500">
                {formatDate(s.createdAtMs)}
                {' · '}
                {formatBytes(s.sizeBytes)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(value: string | number | null): string {
  if (value === null) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
